import React, { useEffect, useState, useRef } from "react";
import { PortalService } from "services/portal";
import * as XLSX from "xlsx";
import Chart from "chart.js/auto";

const CARD = { background: "var(--bg-card)", borderRadius: 12, border: "1px solid var(--border)", overflow: "hidden" };
const MONTHS = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const MONTHS_SHORT = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

function formatCLP(n) {
  if (!n && n !== 0) return "$0";
  return "$" + Math.round(n).toLocaleString("es-CL");
}

function downloadCSV(rows, filename) {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]);
  const escape = (v) => {
    const s = v == null ? "" : String(v);
    return /[",;\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [headers.join(";"), ...rows.map((r) => headers.map((h) => escape(r[h])).join(";"))].join("\n");
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function PortalVentas() {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [yearly, setYearly] = useState(null);
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  useEffect(() => {
    setLoading(true);
    PortalService.getVentas({ month, year })
      .then(setData)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [month, year]);

  useEffect(() => {
    PortalService.getResumen({ year })
      .then((data) => setYearly(Array.isArray(data) ? data : data?.resumen || []))
      .catch(console.error);
  }, [year]);

  useEffect(() => {
    if (!yearly || !chartRef.current) return;
    if (chartInstance.current) chartInstance.current.destroy();

    const labels = MONTHS_SHORT;
    const totals = Array.from({ length: 12 }, (_, i) => {
      const row = (yearly || []).find((r) => r.month === i + 1);
      return row?.ventas?.totalMonto || 0;
    });
    const counts = Array.from({ length: 12 }, (_, i) => {
      const row = (yearly || []).find((r) => r.month === i + 1);
      return row?.ventas?.cantidadDocumentos || 0;
    });

    chartInstance.current = new Chart(chartRef.current, {
      data: {
        labels,
        datasets: [
          { type: "bar", label: "Total ventas", data: totals, backgroundColor: "#10b981", borderRadius: 4, yAxisID: "y" },
          { type: "line", label: "Documentos", data: counts, borderColor: "#f59e0b", backgroundColor: "#fde68a", tension: 0.3, yAxisID: "y1" },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: "top", labels: { usePointStyle: true, padding: 16 } },
          tooltip: {
            callbacks: {
              label: (ctx) => ctx.dataset.yAxisID === "y" ? `${ctx.dataset.label}: ${formatCLP(ctx.raw)}` : `${ctx.dataset.label}: ${ctx.raw}`,
            },
          },
        },
        scales: {
          y: {
            position: "left",
            ticks: { callback: (v) => v >= 1e6 ? `${(v / 1e6).toFixed(1)}M` : v >= 1e3 ? `${(v / 1e3).toFixed(0)}K` : v },
            grid: { color: "#f1f5f9" },
          },
          y1: { position: "right", grid: { drawOnChartArea: false } },
          x: { grid: { display: false } },
        },
      },
    });

    return () => { if (chartInstance.current) chartInstance.current.destroy(); };
  }, [yearly]);

  const buildRows = () => {
    if (!data?.records?.length) return [];
    return data.records.map((r) => ({
      "Tipo Doc": r.tipoDoc,
      "N° Doc": r.numDoc,
      Fecha: r.fechaDoc ? new Date(r.fechaDoc).toLocaleDateString("es-CL") : "",
      RUT: r.rutContraparte,
      "Razon Social": r.razonSocial,
      Exento: r.montoExento,
      Neto: r.montoNeto,
      IVA: r.montoIva,
      Total: r.montoTotal,
      Estado: r.estado,
    }));
  };

  const exportExcel = () => {
    const rows = buildRows();
    if (!rows.length) return;
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Ventas");
    XLSX.writeFile(wb, `Ventas_${MONTHS[month - 1]}_${year}.xlsx`);
  };

  const exportCSV = () => {
    const rows = buildRows();
    if (!rows.length) return;
    downloadCSV(rows, `Ventas_${MONTHS[month - 1]}_${year}.csv`);
  };

  const [syncing, setSyncing] = useState(false);
  const [downloadingDte, setDownloadingDte] = useState(false);
  const triggerSyncMonth = async () => {
    setSyncing(true);
    try {
      await PortalService.triggerSync({ month, year, direction: "VENTAS" });
      setTimeout(() => {
        PortalService.getVentas({ month, year }).then(setData).catch(console.error);
      }, 4000);
    } catch (err) {
      console.error("[triggerSync ventas]", err?.response?.data?.error || err.message);
    } finally {
      setSyncing(false);
    }
  };

  const triggerDteMonth = async () => {
    setDownloadingDte(true);
    try {
      await PortalService.triggerDteSync({ month, year, direction: "EMITIDAS", maxDocuments: 50 });
    } catch (err) {
      console.error("[triggerDte ventas]", err?.response?.data?.error || err.message);
    } finally {
      setDownloadingDte(false);
    }
  };

  const thStyle = { padding: "10px 12px", textAlign: "left", fontSize: 11, fontWeight: 600, color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: "2px solid var(--border)" };
  const tdStyle = { padding: "10px 12px", fontSize: 13, color: "var(--text-secondary)", borderBottom: "1px solid var(--border-light)" };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: "var(--text-primary)", marginBottom: 4 }}>Registro de Ventas</h1>
          <p style={{ fontSize: 14, color: "var(--text-secondary)" }}>Documentos emitidos del SII</p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            onClick={triggerSyncMonth}
            disabled={syncing}
            title="Trae ventas del SII para este mes"
            style={{
              padding: "8px 16px", fontSize: 13, fontWeight: 600, background: syncing ? "#94a3b8" : "#10b981", color: "#fff",
              border: "none", borderRadius: 8, cursor: syncing ? "not-allowed" : "pointer",
              boxShadow: "0 1px 4px rgba(16,185,129,0.25)",
            }}
          >
            {syncing ? "Sincronizando..." : "Sincronizar este mes"}
          </button>
          <button
            onClick={triggerDteMonth}
            disabled={downloadingDte}
            title="Descarga los PDFs (DTEs) emitidos del mes"
            style={{
              padding: "8px 16px", fontSize: 13, fontWeight: 600,
              background: "var(--bg-card)", color: "#10b981",
              border: "1.5px solid #10b981", borderRadius: 8,
              cursor: downloadingDte ? "not-allowed" : "pointer",
              opacity: downloadingDte ? 0.6 : 1,
            }}
          >
            {downloadingDte ? "Iniciando..." : "Descargar PDFs"}
          </button>
          <button
            onClick={exportCSV}
            disabled={!data?.records?.length}
            style={{
              padding: "8px 16px", fontSize: 13, fontWeight: 600, background: "var(--bg-card)", color: "var(--text-primary)",
              border: "1.5px solid var(--border)", borderRadius: 8, cursor: data?.records?.length ? "pointer" : "not-allowed",
              opacity: data?.records?.length ? 1 : 0.5,
            }}
          >
            CSV
          </button>
          <button
            onClick={exportExcel}
            disabled={!data?.records?.length}
            style={{
              padding: "8px 20px", fontSize: 13, fontWeight: 600, background: "#0f172a", color: "#fff",
              border: "none", borderRadius: 8, cursor: data?.records?.length ? "pointer" : "not-allowed",
              opacity: data?.records?.length ? 1 : 0.5,
            }}
          >
            Exportar Excel
          </button>
        </div>
      </div>

      {/* Period selector */}
      <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap", alignItems: "center" }}>
        <select
          value={month}
          onChange={(e) => setMonth(parseInt(e.target.value))}
          style={{ padding: "8px 12px", border: "1.5px solid var(--border)", borderRadius: 8, fontSize: 13, background: "var(--bg-card)", color: "var(--text-secondary)" }}
        >
          {MONTHS.map((m, i) => (
            <option key={i} value={i + 1}>{m}</option>
          ))}
        </select>
        <select
          value={year}
          onChange={(e) => setYear(parseInt(e.target.value))}
          style={{ padding: "8px 12px", border: "1.5px solid var(--border)", borderRadius: 8, fontSize: 13, background: "var(--bg-card)", color: "var(--text-secondary)" }}
        >
          {[2024, 2025, 2026].map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
      </div>

      {/* Tendencia anual */}
      <div style={{ ...CARD, padding: 20, marginBottom: 20 }}>
        <h3 style={{ fontSize: 13, fontWeight: 600, color: "var(--text-secondary)", marginBottom: 12 }}>
          Tendencia ventas {year}
        </h3>
        <div style={{ height: 220 }}>
          <canvas ref={chartRef} />
        </div>
      </div>

      {/* Summary */}
      {data?.summary && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 12, marginBottom: 20 }}>
          {[
            { label: "Documentos", value: data.summary.cantidadDocumentos },
            { label: "Exento", value: formatCLP(data.summary.totalExento) },
            { label: "Neto", value: formatCLP(data.summary.totalNeto) },
            { label: "IVA", value: formatCLP(data.summary.totalIva) },
            { label: "Total", value: formatCLP(data.summary.totalMonto) },
          ].map((s) => (
            <div key={s.label} style={{ ...CARD, padding: "16px 20px" }}>
              <p style={{ fontSize: 11, color: "var(--text-secondary)", fontWeight: 500, marginBottom: 4 }}>{s.label}</p>
              <p style={{ fontSize: 18, fontWeight: 700, color: "var(--text-primary)" }}>{s.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Table */}
      <div style={CARD}>
        {loading ? (
          <div style={{ padding: 40, textAlign: "center", color: "var(--text-muted)" }}>Cargando...</div>
        ) : !data?.records?.length ? (
          <div style={{ padding: 40, textAlign: "center", color: "var(--text-muted)" }}>
            No hay registros de venta para {MONTHS[month - 1]} {year}
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "var(--bg-elevated)" }}>
                  <th style={thStyle}>Tipo Doc</th>
                  <th style={thStyle}>N° Doc</th>
                  <th style={thStyle}>Fecha</th>
                  <th style={thStyle}>RUT</th>
                  <th style={thStyle}>Razon Social</th>
                  <th style={{ ...thStyle, textAlign: "right" }}>Exento</th>
                  <th style={{ ...thStyle, textAlign: "right" }}>Neto</th>
                  <th style={{ ...thStyle, textAlign: "right" }}>IVA</th>
                  <th style={{ ...thStyle, textAlign: "right" }}>Total</th>
                  <th style={thStyle}>Estado</th>
                </tr>
              </thead>
              <tbody>
                {data.records.map((r, i) => (
                  <tr key={i} style={{ transition: "background 0.15s" }} onMouseEnter={(e) => (e.currentTarget.style.background = "var(--bg-elevated)")} onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
                    <td style={tdStyle}>{r.tipoDoc}</td>
                    <td style={tdStyle}>{r.numDoc}</td>
                    <td style={tdStyle}>{r.fechaDoc ? new Date(r.fechaDoc).toLocaleDateString("es-CL") : "-"}</td>
                    <td style={tdStyle}>{r.rutContraparte}</td>
                    <td style={{ ...tdStyle, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.razonSocial}</td>
                    <td style={{ ...tdStyle, textAlign: "right" }}>{formatCLP(r.montoExento)}</td>
                    <td style={{ ...tdStyle, textAlign: "right" }}>{formatCLP(r.montoNeto)}</td>
                    <td style={{ ...tdStyle, textAlign: "right" }}>{formatCLP(r.montoIva)}</td>
                    <td style={{ ...tdStyle, textAlign: "right", fontWeight: 600 }}>{formatCLP(r.montoTotal)}</td>
                    <td style={tdStyle}>
                      <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 4, background: r.estado === "Registrado" ? "#ecfdf5" : "#fef3c7", color: r.estado === "Registrado" ? "#059669" : "#d97706" }}>
                        {r.estado || "-"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
